/**
 * Доля ядра публичного рендерера: вид `price_block` (отдельный блок цены).
 *
 * WEB-2921, заход 2: перенесено из `PublicBlockRenderer.tsx` слово в слово.
 * Метка `case 'price_block'` остаётся в ядре; тело ветки — функция отрисовки
 * `renderPriceBlock(ctx)`, замыкания ядра приходят объектом-контекстом
 * (`CoreRenderContext`). Хуков здесь нет. Доля едет в первом экране вместе с
 * ядром (статический импорт), ленивого куска у неё нет.
 *
 * Денежная подача: сумма — по цене САМОГО блока (`block.stripePriceId`) из
 * цен Stripe проекта; выбор тарифа покупателем эта ветка не читает (лестница
 * `resolvePriceId` — у кнопок оплаты и кошельков).
 */
import React from 'react';
import {
  formatStripePrice,
  findStripePrice,
} from '@/utils/formatStripePrice';
import { resolveBlockBorder } from '../blockBorder';
// WEB-1014: режим показа цены для отдельного блока цены — из стилевого набора.
import { getStylePackById } from '@/themes/stylePacks';
import { derivePriceDisplay } from '@/utils/derivePriceDisplay';
import type { CoreRenderContext } from '../PublicBlockRenderer';

export function renderPriceBlock({
  block,
  stylePackId,
  publicStrings,
  varCtx,
  stripePrices,
}: CoreRenderContext): React.ReactElement | null {
  const price = findStripePrice(stripePrices, block.stripePriceId);
  // WEB-1014: способ ПОДАЧИ цены задаёт стилевой набор — так же, как у
  // карточек тарифов (WEB-925 атом 5). Только отображение: priceId и сумма
  // чекаута не трогаются, арифметика и все откаты — в derivePriceDisplay.
  const priceMode = stylePackId
    ? getStylePackById(stylePackId)?.paywall.priceMode
    : undefined;
  const priceDisplay = price
    ? derivePriceDisplay(price, priceMode, varCtx?.system?.locale)
    : undefined;
  // Режим ничего не изменил (нет пака, делить не на что, триала нет) →
  // одна строка, ровно как вчера.
  const splitPrice =
    priceDisplay !== undefined &&
    (priceDisplay.recalculated ||
      priceDisplay.unitLabel !== '' ||
      priceDisplay.actual !== null);
  // WEB-1907 (добор 2): обе заглушки — на языке воронки. Разделение
  // прежнее: тариф выбран, а сумма не резолвится → слово «Цена»
  // (priceLabel); тарифа нет вовсе → общий фолбэк «цена недоступна»
  // (planNoPriceFallback — та же строка, что у карточки тарифа без
  // цены, второй строки под тот же смысл не заводим).
  const amountLabel = price
    ? formatStripePrice(price)
    : block.stripePriceId
      ? publicStrings.priceLabel
      : publicStrings.planNoPriceFallback;
  return (
    <div
      style={{
        // WEB-1697: рамка блока — из справочника умолчаний
        // (blockBorder.ts). Пусто = прежние `2px solid` акцентом темы и
        // скругление 12px, то есть денежный экран не меняется.
        ...resolveBlockBorder(block),
        padding: '16px 20px',
        marginBottom: 16,
        textAlign: 'center',
        color: 'var(--color-text-primary, inherit)',
      }}
    >
      <div
        style={{
          fontSize: price ? 26 : 13,
          fontWeight: price ? 700 : 400,
          color: price
            ? 'var(--color-text-primary, inherit)'
            : 'var(--color-text-secondary, #8c8c8c)',
        }}
        data-testid="price-block-amount"
      >
        {priceDisplay && splitPrice ? (
          <>
            <div data-testid="price-block-main">{priceDisplay.main}</div>
            {priceDisplay.unitLabel !== '' && (
              <div
                data-testid="price-block-unit"
                style={{ fontSize: 13, fontWeight: 600 }}
              >
                {priceDisplay.unitLabel}
              </div>
            )}
            {priceDisplay.actual !== null && (
              <div
                data-testid="price-block-actual"
                // WEB-1014 п.3: цена после пробного периода — полноценная
                // вторая строка, а не мелкий шрифт под «бесплатно». При
                // пересчёте («в день») крупно подан сам пересчёт, поэтому
                // строка настоящего списания там остаётся подписью — но
                // присутствует на экране всегда.
                style={{
                  fontSize: priceDisplay.actualProminent ? 16 : 13,
                  fontWeight: priceDisplay.actualProminent ? 600 : 400,
                }}
              >
                {priceDisplay.actual}
              </div>
            )}
          </>
        ) : (
          amountLabel
        )}
      </div>
    </div>
  );
}
